/**
 * Luxury Gold Theme Styles
 * 
 * Estilos específicos para o tema Luxo Dourado.
 * Características: gradientes metálicos, tipografia elegante, efeitos shimmer, bordas douradas.
 */

import { CSSProperties } from 'react';

// ============================================
// BASE STYLES
// ============================================

export const luxuryGoldBase: CSSProperties = {
  background: 'var(--lux-bg)',
  color: 'var(--lux-text)',
  fontFamily: 'var(--lux-font-body)',
  borderRadius: 'var(--lux-radius)',
  border: 'var(--lux-border)',
  boxShadow: 'var(--lux-shadow)',
  transition: 'var(--lux-transition)',
};

// ============================================
// INPUT STYLES
// ============================================

export const luxuryGoldInput: CSSProperties = {
  background: 'var(--lux-bg-secondary)',
  color: 'var(--lux-text)',
  border: 'var(--lux-border)',
  borderRadius: 'var(--lux-radius)',
  padding: '14px 18px',
  fontSize: '15px',
  fontFamily: 'var(--lux-font-body)',
  letterSpacing: 'var(--lux-letter-spacing)',
  transition: 'var(--lux-transition)',
  outline: 'none',
};

export const luxuryGoldInputFocus: CSSProperties = {
  border: 'var(--lux-border-active)',
  boxShadow: 'var(--lux-shadow-gold)',
};

export const luxuryGoldInputError: CSSProperties = {
  borderColor: 'var(--lux-danger)',
  boxShadow: '0 0 10px rgba(220, 53, 69, 0.3)',
};

export const luxuryGoldInputDisabled: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  background: 'var(--lux-bg-tertiary)',
};

export const luxuryGoldInputLabel: CSSProperties = {
  fontFamily: 'var(--lux-font-title)',
  fontSize: '13px',
  letterSpacing: 'var(--lux-letter-spacing-wide)',
  textTransform: 'uppercase',
  color: 'var(--lux-text-gold)',
  marginBottom: '8px',
};

// ============================================
// BUTTON STYLES
// ============================================

export const luxuryGoldButton: CSSProperties = {
  background: 'var(--lux-gold)',
  color: 'var(--lux-bg)',
  border: 'none',
  borderRadius: 'var(--lux-radius)',
  padding: '14px 32px',
  fontSize: '14px',
  fontFamily: 'var(--lux-font-body)',
  fontWeight: 'var(--lux-font-weight-bold)' as unknown as number,
  letterSpacing: 'var(--lux-letter-spacing-wide)',
  textTransform: 'uppercase',
  boxShadow: 'var(--lux-shadow-gold)',
  cursor: 'pointer',
  transition: 'var(--lux-transition)',
  outline: 'none',
  position: 'relative',
  overflow: 'hidden',
};

export const luxuryGoldButtonHover: CSSProperties = {
  boxShadow: 'var(--lux-shadow-gold-lg)',
  transform: 'translateY(-2px)',
};

export const luxuryGoldButtonActive: CSSProperties = {
  transform: 'translateY(0)',
  boxShadow: 'var(--lux-shadow-gold)',
};

export const luxuryGoldButtonSecondary: CSSProperties = {
  background: 'transparent',
  color: 'var(--lux-gold-solid)',
  border: 'var(--lux-border-gold)',
  boxShadow: 'none',
};

export const luxuryGoldButtonSecondaryHover: CSSProperties = {
  background: 'rgba(212, 175, 55, 0.1)',
  boxShadow: 'var(--lux-glow-gold)',
};

export const luxuryGoldButtonGhost: CSSProperties = {
  background: 'transparent',
  color: 'var(--lux-text)',
  border: 'var(--lux-border)',
  boxShadow: 'none',
};

export const luxuryGoldButtonDanger: CSSProperties = {
  background: 'var(--lux-danger)',
  color: '#ffffff',
  boxShadow: '0 4px 20px rgba(220, 53, 69, 0.3)',
};

export const luxuryGoldButtonDisabled: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  transform: 'none',
};

// Shimmer effect overlay (aplicar via pseudo-elemento)
export const luxuryGoldButtonShimmer: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--lux-shimmer)',
  backgroundSize: 'var(--lux-shimmer-size)',
  animation: 'var(--lux-shimmer-animation)',
  pointerEvents: 'none',
};

// ============================================
// CARD STYLES
// ============================================

export const luxuryGoldCard: CSSProperties = {
  background: 'var(--lux-bg-card)',
  color: 'var(--lux-text)',
  borderRadius: 'var(--lux-radius-lg)',
  border: 'var(--lux-border)',
  boxShadow: 'var(--lux-shadow)',
  padding: 'var(--lux-spacing-xl)',
  transition: 'var(--lux-transition)',
};

export const luxuryGoldCardHover: CSSProperties = {
  border: 'var(--lux-border-hover)',
  boxShadow: 'var(--lux-shadow-lg), var(--lux-glow-gold)',
};

export const luxuryGoldCardHeader: CSSProperties = {
  borderBottom: 'var(--lux-border)',
  paddingBottom: 'var(--lux-spacing-md)',
  marginBottom: 'var(--lux-spacing-lg)',
};

export const luxuryGoldCardTitle: CSSProperties = {
  fontFamily: 'var(--lux-font-title)',
  fontSize: '1.5rem',
  color: 'var(--lux-text-gold)',
  letterSpacing: 'var(--lux-letter-spacing)',
};

export const luxuryGoldCardElevated: CSSProperties = {
  ...luxuryGoldCard,
  background: 'var(--lux-bg-elevated)',
  boxShadow: 'var(--lux-shadow-lg)',
};

export const luxuryGoldCardGoldBorder: CSSProperties = {
  ...luxuryGoldCard,
  border: 'var(--lux-border-gold)',
  boxShadow: 'var(--lux-shadow), var(--lux-glow-gold)',
};

// ============================================
// SELECT STYLES
// ============================================

export const luxuryGoldSelect: CSSProperties = {
  background: 'var(--lux-bg-secondary)',
  color: 'var(--lux-text)',
  border: 'var(--lux-border)',
  borderRadius: 'var(--lux-radius)',
  padding: '14px 44px 14px 18px',
  fontSize: '15px',
  fontFamily: 'var(--lux-font-body)',
  letterSpacing: 'var(--lux-letter-spacing)',
  cursor: 'pointer',
  transition: 'var(--lux-transition)',
  appearance: 'none',
};

export const luxuryGoldSelectDropdown: CSSProperties = {
  background: 'var(--lux-bg-elevated)',
  borderRadius: 'var(--lux-radius)',
  border: 'var(--lux-border)',
  boxShadow: 'var(--lux-shadow-lg)',
  marginTop: '8px',
  overflow: 'hidden',
};

export const luxuryGoldSelectOption: CSSProperties = {
  padding: '14px 18px',
  cursor: 'pointer',
  transition: 'var(--lux-transition-fast)',
  borderBottom: 'var(--lux-border)',
};

export const luxuryGoldSelectOptionHover: CSSProperties = {
  background: 'rgba(212, 175, 55, 0.1)',
  color: 'var(--lux-text-gold)',
};

export const luxuryGoldSelectOptionSelected: CSSProperties = {
  background: 'var(--lux-gold)',
  color: 'var(--lux-bg)',
};

// ============================================
// TABLE STYLES
// ============================================

export const luxuryGoldTable: CSSProperties = {
  background: 'var(--lux-bg-card)',
  borderRadius: 'var(--lux-radius-lg)',
  border: 'var(--lux-border)',
  boxShadow: 'var(--lux-shadow)',
  overflow: 'hidden',
};

export const luxuryGoldTableHeader: CSSProperties = {
  background: 'var(--lux-bg-elevated)',
  color: 'var(--lux-text-gold)',
  padding: '16px 20px',
  fontFamily: 'var(--lux-font-title)',
  fontWeight: 'var(--lux-font-weight-bold)' as unknown as number,
  letterSpacing: 'var(--lux-letter-spacing-wide)',
  textTransform: 'uppercase',
  fontSize: '12px',
  borderBottom: 'var(--lux-border)',
  textAlign: 'left',
};

export const luxuryGoldTableRow: CSSProperties = {
  borderBottom: 'var(--lux-border)',
  transition: 'var(--lux-transition-fast)',
};

export const luxuryGoldTableRowHover: CSSProperties = {
  background: 'rgba(212, 175, 55, 0.05)',
};

export const luxuryGoldTableCell: CSSProperties = {
  padding: '16px 20px',
  color: 'var(--lux-text)',
};

// ============================================
// MODAL STYLES
// ============================================

export const luxuryGoldModalOverlay: CSSProperties = {
  background: 'rgba(0, 0, 0, 0.85)',
  backdropFilter: 'blur(8px)',
};

export const luxuryGoldModal: CSSProperties = {
  background: 'var(--lux-bg-card)',
  borderRadius: 'var(--lux-radius-xl)',
  border: 'var(--lux-border)',
  boxShadow: 'var(--lux-shadow-lg), var(--lux-glow-gold)',
  padding: 'var(--lux-spacing-xl)',
};

export const luxuryGoldModalHeader: CSSProperties = {
  borderBottom: 'var(--lux-border)',
  paddingBottom: 'var(--lux-spacing-md)',
  marginBottom: 'var(--lux-spacing-lg)',
};

export const luxuryGoldModalTitle: CSSProperties = {
  fontFamily: 'var(--lux-font-title)',
  fontSize: '1.75rem',
  color: 'var(--lux-text-gold)',
  letterSpacing: 'var(--lux-letter-spacing)',
};

// ============================================
// TABS STYLES
// ============================================

export const luxuryGoldTabs: CSSProperties = {
  display: 'flex',
  borderBottom: 'var(--lux-border)',
  gap: '0',
};

export const luxuryGoldTab: CSSProperties = {
  padding: '14px 28px',
  background: 'transparent',
  color: 'var(--lux-text-secondary)',
  border: 'none',
  borderBottom: '2px solid transparent',
  cursor: 'pointer',
  fontFamily: 'var(--lux-font-body)',
  fontSize: '14px',
  letterSpacing: 'var(--lux-letter-spacing)',
  textTransform: 'uppercase',
  transition: 'var(--lux-transition)',
};

export const luxuryGoldTabActive: CSSProperties = {
  color: 'var(--lux-text-gold)',
  borderBottomColor: 'var(--lux-gold-solid)',
};

export const luxuryGoldTabHover: CSSProperties = {
  color: 'var(--lux-text)',
};

// ============================================
// BADGE STYLES
// ============================================

export const luxuryGoldBadge: CSSProperties = {
  background: 'var(--lux-gold)',
  color: 'var(--lux-bg)',
  borderRadius: 'var(--lux-radius-sm)',
  padding: '6px 14px',
  fontSize: '12px',
  fontFamily: 'var(--lux-font-body)',
  fontWeight: 'var(--lux-font-weight-bold)' as unknown as number,
  letterSpacing: 'var(--lux-letter-spacing)',
  textTransform: 'uppercase',
};

export const luxuryGoldBadgeSuccess: CSSProperties = {
  background: 'var(--lux-success)',
  color: 'var(--lux-bg)',
};

export const luxuryGoldBadgeWarning: CSSProperties = {
  background: 'var(--lux-warning)',
  color: 'var(--lux-bg)',
};

export const luxuryGoldBadgeDanger: CSSProperties = {
  background: 'var(--lux-danger)',
  color: '#ffffff',
};

export const luxuryGoldBadgeInfo: CSSProperties = {
  background: 'var(--lux-info)',
  color: 'var(--lux-bg)',
};

export const luxuryGoldBadgeOutline: CSSProperties = {
  background: 'transparent',
  color: 'var(--lux-gold-solid)',
  border: '1px solid var(--lux-gold-solid)',
};

// ============================================
// TOOLTIP STYLES
// ============================================

export const luxuryGoldTooltip: CSSProperties = {
  background: 'var(--lux-bg-elevated)',
  color: 'var(--lux-text)',
  borderRadius: 'var(--lux-radius-sm)',
  border: 'var(--lux-border)',
  padding: '10px 14px',
  fontSize: '13px',
  fontFamily: 'var(--lux-font-body)',
  boxShadow: 'var(--lux-shadow)',
};

// ============================================
// METAL VARIANTS
// ============================================

// Gold (default)
export const luxuryGoldMetalGold: CSSProperties = {
  background: 'var(--lux-gold)',
};

// Silver
export const luxuryGoldMetalSilver: CSSProperties = {
  background: 'var(--lux-silver)',
  '--lux-gold-solid': '#c0c0c0',
  '--lux-text-gold': '#c0c0c0',
  '--lux-border': '1px solid rgba(192, 192, 192, 0.3)',
  '--lux-glow-gold': '0 0 20px rgba(192, 192, 192, 0.4)',
} as CSSProperties;

// Rose Gold
export const luxuryGoldMetalRoseGold: CSSProperties = {
  background: 'var(--lux-rose-gold)',
  '--lux-gold-solid': '#b76e79',
  '--lux-text-gold': '#ecc5c0',
  '--lux-border': '1px solid rgba(183, 110, 121, 0.3)',
  '--lux-glow-gold': '0 0 20px rgba(183, 110, 121, 0.4)',
} as CSSProperties;

// Platinum
export const luxuryGoldMetalPlatinum: CSSProperties = {
  background: 'var(--lux-platinum)',
  '--lux-gold-solid': '#e5e4e2',
  '--lux-text-gold': '#e5e4e2',
  '--lux-border': '1px solid rgba(229, 228, 226, 0.3)',
  '--lux-glow-gold': '0 0 20px rgba(229, 228, 226, 0.4)',
} as CSSProperties;

// ============================================
// DECORATIVE ELEMENTS
// ============================================

export const luxuryGoldDivider: CSSProperties = {
  height: '1px',
  background: 'var(--lux-gold)',
  margin: 'var(--lux-spacing-lg) 0',
};

export const luxuryGoldGradientText: CSSProperties = {
  background: 'var(--lux-gold)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

export const luxuryGoldShimmerOverlay: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--lux-shimmer)',
  backgroundSize: 'var(--lux-shimmer-size)',
  animation: 'var(--lux-shimmer-animation)',
  pointerEvents: 'none',
};

